import React from "react"
import {
  Skeleton,
  SkeletonLines,
  TableSkeleton,
} from "components/new-stuff/ui/Skeleton"

/**
 * Placeholders for the payroll submit and report pages, each drawn in the shape of the page it
 * stands in for.
 *
 * All three used to show something else entirely while loading: a blank white box behind a
 * spinner, a full-screen spinner, and a one-column table. The real pages are a stepped form, a
 * printed-looking report, and a wide table grouped under coloured section headers, so the
 * handover was a jump every time.
 *
 * Words that never change -- headings, step names, panel titles, button labels -- are drawn as
 * words. Only what the data decides is grey.
 */

/* -------------------------------------------------------------- submit page -- */

/** One collapsed panel row, as StepOne draws "What your employees..." and "What your company pays". */
const CollapsedPanel = ({ title }: { title: string }) => (
  <div className="mb-[14px] flex items-center justify-between rounded-lg border-2 border-cardgrey bg-white px-4 py-6 lg:px-[18px]">
    <p className="m-0 font-circular text-base font-medium text-padeBlack">
      {title}
    </p>
    <span className="h-7 w-7 rounded-full border border-neutral50" />
  </div>
)

/**
 * The body of "Submit <period>": the step indicator and the Payroll Items card, with its two
 * panels collapsed as they open. Drawn inside the page's own container, below its own top bar.
 */
export const PayrollSubmitSkeleton = () => (
  <div aria-busy="true">
    {/* The two steps, the first current. */}
    <div className="mt-6 flex max-w-full gap-6 pl-4 pr-4 lg:max-w-[800px] lg:pl-8 xl:pl-12">
      {["Payroll Items", "Review and Submit"].map((step, index) => (
        <div
          key={step}
          className="flex flex-1 flex-col items-center gap-2"
        >
          <span
            className={`grid h-8 w-8 place-content-center rounded-full font-avenir text-sm ${
              index === 0
                ? "bg-primary500 text-white"
                : "border border-neutral50 text-neutral300"
            }`}
          >
            {index + 1}
          </span>
          <span className="font-avenir text-sm text-neutral300">{step}</span>
        </div>
      ))}
    </div>

    <div className="mb-10 mt-4">
      <div className="flex w-full flex-col gap-7 rounded-lg border bg-white pt-[30px]">
        <div className="px-4 md:px-6 lg:px-8 xl:px-[60px]">
          <h5 className="m-0 font-circular text-base font-bold text-neutral500">
            Payroll Items
          </h5>
        </div>

        <div className="w-full border-t pb-12 pt-6">
          <div className="mb-6 px-4 md:px-6 lg:px-8 xl:px-[60px]">
            {/* The explanation, the run-date warning, then the skip banner. */}
            <SkeletonLines lines={4} />
            <div className="mt-4">
              <SkeletonLines lines={2} />
            </div>
            <div className="mt-4">
              <Skeleton
                variant="block"
                height="52px"
              />
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 xl:px-[60px]">
            <CollapsedPanel title="What your employees worked and take home" />
            <CollapsedPanel title="What your company pays" />
          </div>

          <div className="-mb-6 mt-12 flex justify-end gap-3 border-t px-4 pt-4 md:px-6 lg:px-8 xl:px-[60px]">
            <Skeleton
              variant="block"
              width="188px"
              height="40px"
            />
            <Skeleton
              variant="block"
              width="72px"
              height="40px"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
)

/* ---------------------------------------------------------- summary report -- */

/**
 * The document area of the payroll summary report: created date, the company's address
 * block on the right, the period line, then the employee table and the earnings summary.
 * The page's own frame -- the back and download bar, and the title -- is drawn around it.
 */
export const PayrollSummaryReportSkeleton = () => (
  <div aria-busy="true">
    <div className="px-6">
      <Skeleton
        width="220px"
        height="14px"
      />
    </div>

    <div className="my-4 flex flex-col items-end gap-2 px-6">
      <Skeleton width="160px" />
      <Skeleton width="220px" />
      <Skeleton width="190px" />
    </div>

    <div className="my-4 px-6">
      <Skeleton width="280px" />
    </div>

    {/* One row per employee, and wide: the columns are every earning in the run. */}
    <div className="px-6">
      <TableSkeleton
        rows={10}
        columns={8}
      />
    </div>

    {/* The earnings summary: description, amount. */}
    <div className="my-4 px-6">
      <TableSkeleton
        rows={5}
        columns={3}
      />
    </div>
  </div>
)

/* ---------------------------------------------------------- journal report -- */

/** How much of the page each section takes: the person, then earnings, deductions, summary. */
const JOURNAL_SECTION_WIDTHS = ["28%", "26%", "24%", "22%"]

/**
 * The payroll journal while it loads, in the compact or detailed layout: the filter toolbar,
 * then a table whose header is grouped under coloured section bands -- employee details,
 * earnings, deductions, summary.
 *
 * The table builds its columns from the report itself, so before the report arrives it has
 * almost none, and its own loading state drew one narrow column.
 */
export const PayrollJournalSkeleton = ({
  rows = 8,
  bands,
}: {
  rows?: number
  /**
   * The section header colours, in column order. The page passes its own, which a person can
   * customise, so the placeholder is already the colour the table will be. Softened, because a
   * placeholder at full strength reads as content.
   */
  bands: string[]
}) => (
  <div
    className="flex flex-col gap-4"
    aria-busy="true"
  >
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex gap-3">
        <Skeleton
          variant="block"
          width="200px"
          height="36px"
        />
        <Skeleton
          variant="block"
          width="150px"
          height="36px"
        />
      </div>
      <Skeleton
        variant="block"
        width="170px"
        height="36px"
      />
    </div>

    <div className="overflow-hidden rounded border border-neutral40">
      {/* Section bands, then the column labels under them. */}
      <div className="flex">
        {JOURNAL_SECTION_WIDTHS.map((width, index) => (
          <div
            key={index}
            className="h-9 border-r border-white last:border-r-0"
            style={{
              width,
              backgroundColor: bands[index] ?? "#dfe2e6",
              opacity: 0.55,
            }}
          />
        ))}
      </div>
      <div className="flex border-b border-neutral40 bg-neutral20">
        {JOURNAL_SECTION_WIDTHS.map((width, index) => (
          <div
            key={index}
            className="flex gap-3 border-r border-neutral40 px-3 py-3 last:border-r-0"
            style={{ width }}
          >
            <Skeleton width="55%" />
            <Skeleton width="30%" />
          </div>
        ))}
      </div>

      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          className="flex border-b border-neutral40 last:border-b-0"
        >
          {JOURNAL_SECTION_WIDTHS.map((width, index) => (
            <div
              key={index}
              className="flex flex-col gap-2 border-r border-neutral40 px-3 py-3 last:border-r-0"
              style={{ width }}
            >
              {/* Each cell lists a few lines -- name and number, or item and amount. */}
              <Skeleton width={index === 0 ? "70%" : "80%"} />
              <Skeleton width={index === 0 ? "45%" : "60%"} />
              {index !== 0 ? <Skeleton width="50%" /> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
)

/**
 * The journal's Summary tab while it loads: the name and date lines, then a few small
 * element / amount / count tables, each with its total underneath.
 */
export const PayrollJournalSummarySkeleton = () => (
  <div
    className="space-y-6"
    aria-busy="true"
  >
    <div className="grid gap-2 md:grid-cols-2">
      <Skeleton width="60%" />
      <Skeleton width="50%" />
      <div className="md:col-span-2">
        <Skeleton width="40%" />
      </div>
    </div>

    {Array.from({ length: 3 }, (_, index) => (
      <div
        key={index}
        className="space-y-2"
      >
        <Skeleton
          width="120px"
          height="16px"
        />
        <TableSkeleton
          rows={4}
          columns={3}
        />
        <div className="flex justify-end border border-neutral200 px-4 py-2">
          <Skeleton width="200px" />
        </div>
      </div>
    ))}
  </div>
)
